'use client';

import { forwardRef, ReactNode } from "react";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";
import { labelClasses } from "./classes";
import { StyledLabel } from "./styledLabel";
import type { LabelColor, LabelVariant } from "./types";

interface LabelProps extends React.ComponentPropsWithoutRef<"span"> {
  children: ReactNode;
  color?: LabelColor;
  variant?: LabelVariant;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  sx?: SxProps<Theme>;
  className?: string;
}

export const CustomLabel = forwardRef<HTMLSpanElement, LabelProps>(
  ({ children, color = "default", variant = "soft", startIcon, endIcon, sx, className, ...other }, ref) => {
    const theme = useTheme();

    return (
      <StyledLabel
        ref={ref}
        component="span"
        className={labelClasses.root.concat(className ? ` ${className}` : "")}
        ownerState={{ color, variant }} // ✅ Now properly typed!
        sx={{
          ...(startIcon && { pl: 0.75 }),
          ...(endIcon && { pr: 0.75 }),
          ...sx,
        }}
        theme={theme}
        {...other}
      >
        {startIcon && (
          <Box component="span" className={labelClasses.icon} sx={{ mr: 0.75 }}>
            {startIcon}
          </Box>
        )}

        {children}

        {endIcon && (
          <Box component="span" className={labelClasses.icon} sx={{ ml: 0.75 }}>
            {endIcon}
          </Box>
        )}
      </StyledLabel>
    );
  }
);

CustomLabel.displayName = "Label";
